import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Eye, EyeOff } from 'lucide-react';
import { authService } from '../services/AuthService';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token') ?? '';
  const urlEmail = searchParams.get('email') ?? '';
  const prefillEmail = urlEmail || ((location.state as { email?: string } | null)?.email ?? '');

  const [email, setEmail] = useState(prefillEmail);
  const [token, setToken] = useState(urlToken);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.resetPassword({ email, token, newPassword });
      navigate('/login');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Något gick fel');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-700 mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Återställ lösenord</h1>
          <p className="text-gray-500 mt-1">Ange koden du fick via e-post och ditt nya lösenord</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1.5">E-postadress</label>
            <input
              id="reset-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              placeholder="din@epost.se"
            />
          </div>

          {!urlToken && (
            <div>
              <label htmlFor="reset-token" className="block text-sm font-medium text-gray-700 mb-1.5">Återställningskod</label>
              <input
                id="reset-token"
                type="text"
                value={token}
                onChange={e => setToken(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                placeholder="Klistra in koden från e-postmeddelandet"
              />
            </div>
          )}

          <div>
            <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700 mb-1.5">Nytt lösenord</label>
            <div className="relative">
              <input
                id="reset-password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                minLength={10}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all pr-10"
                placeholder="Minst 10 tecken"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-teal-700 hover:bg-teal-800 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Återställer...' : 'Återställ lösenord'}
          </button>

          <p className="text-center text-sm text-gray-500">
            <Link to="/forgot-password" className="text-teal-700 hover:text-teal-800 font-medium">
              Skicka ny kod
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
